import React from "react";
import { useTranslation } from "react-i18next";
import { MelaChip } from "@/ui";
import { MelaTooltip } from "@/ui/mela/MelaTooltip";
import { safetyColors } from "./tokens";

export type DocumentStatusKey =
  | "PRESENT"
  | "EXPIRING"
  | "EXPIRED"
  | "MISSING"
  | "valid"
  | "expiring"
  | "expired"
  | "missing";

export type AggregateStatusKey = "OK" | "WARNING" | "BLOCKED";

const DOC_STATUS = {
  PRESENT: {
    labelKey: "safety.status.doc.present.translation",
    hintKey: "safety.status.doc.present.hint.translation",
    bg: safetyColors.okBg,
    color: safetyColors.ok,
    border: safetyColors.okBorder,
  },
  EXPIRING: {
    labelKey: "safety.status.doc.expiring.translation",
    hintKey: "safety.status.doc.expiring.hint.translation",
    bg: safetyColors.warningBg,
    color: safetyColors.warning,
    border: safetyColors.warningBorder,
  },
  EXPIRED: {
    labelKey: "safety.status.doc.expired.translation",
    hintKey: "safety.status.doc.expired.hint.translation",
    bg: safetyColors.dangerBg,
    color: safetyColors.danger,
    border: safetyColors.dangerBorder,
  },
  MISSING: {
    labelKey: "safety.status.doc.missing.translation",
    hintKey: "safety.status.doc.missing.hint.translation",
    bg: safetyColors.surface,
    color: safetyColors.mutedDark,
    border: safetyColors.border,
  },
} as const;

const AGGREGATE_STATUS = {
  OK: {
    labelKey: "safety.status.aggregate.ok.translation",
    hintKey: "safety.status.aggregate.ok.hint.translation",
    bg: safetyColors.okBg,
    color: safetyColors.ok,
    border: safetyColors.okBorder,
  },
  WARNING: {
    labelKey: "safety.status.aggregate.warning.translation",
    hintKey: "safety.status.aggregate.warning.hint.translation",
    bg: safetyColors.warningBg,
    color: safetyColors.warning,
    border: safetyColors.warningBorder,
  },
  BLOCKED: {
    labelKey: "safety.status.aggregate.blocked.translation",
    hintKey: "safety.status.aggregate.blocked.hint.translation",
    bg: safetyColors.dangerBg,
    color: safetyColors.danger,
    border: safetyColors.dangerBorder,
  },
} as const;

/**
 * Every pill of the module is a MelaChip: the shape the rest of the app uses
 * for statuses, in the colours Safety already gives each state.
 */
export const safetyPillPalette = (
  bg: string,
  color: string,
  border?: string,
) => ({
  backgroundColor: bg,
  textColor: color,
  borderColor: border ?? "transparent",
});

export const StatusBadge: React.FC<{
  status: string | null | undefined;
  size?: "sm" | "md";
  withTooltip?: boolean;
  /** Overrides the status's own (translated) hint. */
  hint?: string;
}> = ({ status, size = "sm", withTooltip = false, hint }) => {
  const { t } = useTranslation();
  if (!status) return null;
  const key = status.toUpperCase() as keyof typeof DOC_STATUS;
  const cfg = DOC_STATUS[key];
  if (!cfg) return null;
  const badge = (
    <MelaChip
      size={size}
      palette={safetyPillPalette(cfg.bg, cfg.color, cfg.border)}
    >
      {t(cfg.labelKey)}
    </MelaChip>
  );
  if (!withTooltip && !hint) return badge;
  return <MelaTooltip content={hint ?? t(cfg.hintKey)}>{badge}</MelaTooltip>;
};

export const AggregateBadge: React.FC<{
  status: AggregateStatusKey | string | null | undefined;
  blockingCategories?: readonly string[] | null;
}> = ({ status, blockingCategories }) => {
  const { t } = useTranslation();
  if (!status) return null;
  const cfg = AGGREGATE_STATUS[status as AggregateStatusKey];
  if (!cfg) return null;
  const blockers = blockingCategories ?? [];
  const named = blockers.length > 0;
  return (
    <MelaTooltip
      content={
        named
          ? blockers
              .map((c) =>
                t("safety.status.blocker.item.translation", {
                  category: c,
                }),
              )
              .join(", ")
          : t(cfg.hintKey)
      }
    >
      <MelaChip palette={safetyPillPalette(cfg.bg, cfg.color, cfg.border)}>
        {t(cfg.labelKey)}
      </MelaChip>
    </MelaTooltip>
  );
};

export const NeutralPill: React.FC<{ label: string }> = ({ label }) => (
  <MelaChip
    palette={safetyPillPalette(
      safetyColors.surface,
      safetyColors.textStrong,
      safetyColors.border,
    )}
  >
    {label}
  </MelaChip>
);

type EntityStatusPalette = Record<string, { bg: string; color: string }>;

const SUBCONTRACTOR_STATUS_COLORS: EntityStatusPalette = {
  ACTIVE: { bg: safetyColors.okBg, color: safetyColors.ok },
  SUSPENDED: { bg: safetyColors.warningBg, color: safetyColors.warning },
  INACTIVE: { bg: safetyColors.surface, color: safetyColors.mutedDark },
};

const ASSET_STATUS_COLORS: EntityStatusPalette = {
  ACTIVE: { bg: safetyColors.okBg, color: safetyColors.ok },
  INACTIVE: { bg: safetyColors.surface, color: safetyColors.mutedDark },
  SCRAPPED: { bg: safetyColors.dangerBg, color: safetyColors.danger },
};

const ENTITY_STATUS_LABEL_KEYS: Record<string, string> = {
  ACTIVE: "safety.status.entity.active.translation",
  INACTIVE: "safety.status.entity.inactive.translation",
  SUSPENDED: "safety.status.entity.suspended.translation",
  DISMISSED: "safety.status.entity.dismissed.translation",
};

const EntityStatusChip: React.FC<{
  status: string;
  palette: EntityStatusPalette;
}> = ({ status, palette }) => {
  const { t } = useTranslation();
  const style = palette[status] ?? palette.INACTIVE;
  const labelKey = ENTITY_STATUS_LABEL_KEYS[status];
  return (
    <MelaChip palette={safetyPillPalette(style.bg, style.color)}>
      {labelKey ? t(labelKey) : status}
    </MelaChip>
  );
};

export const SubcontractorStatusChip: React.FC<{ status: string }> = ({
  status,
}) => (
  <EntityStatusChip status={status} palette={SUBCONTRACTOR_STATUS_COLORS} />
);

export const AssetStatusChip: React.FC<{ status: string }> = ({ status }) => (
  <EntityStatusChip status={status} palette={ASSET_STATUS_COLORS} />
);
